import { Euler, Matrix4, Quaternion, Vector3 } from 'three'

/**
 * Integridad geométrica de unidades.
 *
 * Algoritmo: Separating Axis Theorem (SAT) sobre dos OBB.
 * Ejes probados: 3 del primer volumen, 3 del segundo y los 9 productos
 * cruzados de sus aristas. Un eje degenerado (producto cruzado casi nulo,
 * cajas paralelas) se omite.
 *
 * Tolerancia: VOLUME_EPSILON_MM (1 mm). Hay intersección solo si el solape
 * a lo largo de TODOS los ejes supera ese valor. Un solape menor o igual
 * se trata como separación o como contacto de cara/arista/punto, no como
 * volumen común. Así un error de punto flotante del orden de 1e-4 mm no
 * marca una colisión, y un encaje a tope (A|B) sigue siendo válido.
 *
 * El origen de la matriz es la esquina local (0, 0, 0). El volumen local
 * es [0, width] × [0, height] × [0, thickness]. El centro local
 * (width/2, height/2, thickness/2) se transforma con la matriz completa
 * (traslación, rotación y escala). Los semiejes usan el valor absoluto de
 * la escala descompuesta, porque la convención de Three.js (T · R · S)
 * aplica la escala en el espacio local antes de rotar.
 */
export const VOLUME_EPSILON_MM = 1

export type UnitGeometry = {
  id?: string
  type?: string
  visible?: boolean
  canCollide?: boolean
  matrix?: Matrix4 | null
  width?: number
  height?: number
  thickness?: number
  x?: number
  y?: number
  z?: number
  rotation?: number
  snappedSides?: unknown
  catalogID?: string
  firstParentID?: unknown
}

export type UnitOBB = {
  center: Vector3
  halfSize: Vector3
  axes: [Vector3, Vector3, Vector3]
}

const _pos = new Vector3()
const _quat = new Quaternion()
const _scale = new Vector3()
const _localCenter = new Vector3()

/** Unidades que ocupan volumen propio en la escena. Los hijos embebidos (linkedSon), los invisibles y canCollide === false no entran: viven dentro del padre o no ocupan planta. */
export function isOccupyingUnit(item: UnitGeometry | null | undefined): boolean {
  if (!item || !item.matrix) return false
  if (item.type !== 'linkedItem' && item.type !== 'items') return false
  if (item.visible === false) return false
  if (item.canCollide === false) return false
  const width = Number(item.width) || 0
  const height = Number(item.height) || 0
  const thickness = Number(item.thickness) || 0
  return width > 0 && height > 0 && thickness > 0
}

/** OBB del volumen real. El pivote de matrix es la esquina, no el centro. */
export function getUnitOBB(unit: UnitGeometry, matrixOverride?: Matrix4 | null): UnitOBB | null {
  const matrix = matrixOverride ?? unit.matrix
  if (!matrix) return null
  const width = Number(unit.width) || 0
  const height = Number(unit.height) || 0
  const thickness = Number(unit.thickness) || 0
  if (width <= 0 || height <= 0 || thickness <= 0) return null

  matrix.decompose(_pos, _quat, _scale)
  _localCenter.set(width / 2, height / 2, thickness / 2)
  const center = _localCenter.clone().applyMatrix4(matrix)

  const axisX = new Vector3(1, 0, 0).applyQuaternion(_quat).normalize()
  const axisY = new Vector3(0, 1, 0).applyQuaternion(_quat).normalize()
  const axisZ = new Vector3(0, 0, 1).applyQuaternion(_quat).normalize()

  return {
    center,
    halfSize: new Vector3(
      Math.abs(width * _scale.x) / 2,
      Math.abs(height * _scale.y) / 2,
      Math.abs(thickness * _scale.z) / 2,
    ),
    axes: [axisX, axisY, axisZ],
  }
}

function projectedRadius(obb: UnitOBB, axis: Vector3): number {
  return (
    obb.halfSize.x * Math.abs(axis.dot(obb.axes[0])) +
    obb.halfSize.y * Math.abs(axis.dot(obb.axes[1])) +
    obb.halfSize.z * Math.abs(axis.dot(obb.axes[2]))
  )
}

/** Solape mínimo en mm. Negativo = separación. Cero ≈ contacto. */
export function obbOverlapMargin(a: UnitOBB, b: UnitOBB): number {
  const delta = b.center.clone().sub(a.center)
  const axes: Vector3[] = [...a.axes, ...b.axes]
  for (const axisA of a.axes) {
    for (const axisB of b.axes) {
      const cross = axisA.clone().cross(axisB)
      if (cross.lengthSq() > 1e-10) axes.push(cross.normalize())
    }
  }

  let minOverlap = Number.POSITIVE_INFINITY
  for (const axis of axes) {
    if (axis.lengthSq() < 1e-10) continue
    const overlap = projectedRadius(a, axis) + projectedRadius(b, axis) - Math.abs(delta.dot(axis))
    if (overlap < minOverlap) minOverlap = overlap
  }
  return minOverlap
}

/** true solo si el volumen común supera VOLUME_EPSILON_MM. El contacto de cara no cuenta. */
export function obbIntersects(a: UnitOBB, b: UnitOBB): boolean {
  return obbOverlapMargin(a, b) > VOLUME_EPSILON_MM
}

export type UnitGeometryDump = {
  unitId: string
  position: { x: number; y: number; z: number }
  rotationY: number
  scale: { x: number; y: number; z: number }
  width: number
  height: number
  thickness: number
  matrix: number[] | null
  snappedSides: unknown
}

export function dumpUnitGeometry(unit: UnitGeometry, id?: string): UnitGeometryDump {
  const pos = new Vector3()
  const quat = new Quaternion()
  const scale = new Vector3(1, 1, 1)
  if (unit.matrix) unit.matrix.decompose(pos, quat, scale)
  const euler = new Euler().setFromQuaternion(quat, 'YXZ')
  return {
    unitId: String(id ?? unit.id ?? ''),
    position: { x: pos.x, y: pos.y, z: pos.z },
    rotationY: euler.y,
    scale: { x: scale.x, y: scale.y, z: scale.z },
    width: Number(unit.width) || 0,
    height: Number(unit.height) || 0,
    thickness: Number(unit.thickness) || 0,
    matrix: unit.matrix ? Array.from(unit.matrix.elements) : null,
    snappedSides: unit.snappedSides ?? null,
  }
}
